import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import {
  DatePicker,
  FormLayout,
  MoneyInput,
  FormRow as Row,
  FormSection as Section,
  Select,
  TextArea,
  TextInput,
} from '~/components';
import { ButtonFooter } from '~/components/buttons/ButtonFooter';
import { useCustomForm } from '~/hooks/useCustomForm';
import { useServerWarnings } from '~/hooks/useServerWarnings';
import {
  CLAIM_STATUS_OPTIONS,
  MALPRACTICE_CLAIM_MIN_DATE,
  PROVIDER_INVOLVEMENT_OPTIONS,
  SETTLED_CLAIM_STATUSES,
} from './constants';
import { malpracticeClaimSchema } from './schemas';
import { CreateMalpracticeClaimFormValues, MalpracticeClaimFormValues } from './types';
import { errorHandler } from './utils';

const TAGS = CT.malpracticeClaims.form;

const VALID_FIELDS = [
  'claimStatus',
  'occurrenceDate',
  'claimDate',
  'settlementDate',
] as (keyof MalpracticeClaimFormValues)[];

interface Props {
  form: Partial<MalpracticeClaimFormValues>;
  create: boolean;
  addClaim: (values: CreateMalpracticeClaimFormValues) => void;
  editClaim: (values: MalpracticeClaimFormValues) => void;
  handleClose: () => void;
}

export const ClaimsForm = (props: Props) => {
  const { form: claimForm, create, addClaim, editClaim, handleClose } = props;

  const methods = useCustomForm<MalpracticeClaimFormValues>({
    resolver: zodResolver(malpracticeClaimSchema),
    defaultValues: claimForm,
  });

  const { handleSubmit, control, setValue, trigger, formState } = methods;

  const onSubmitError = (err: unknown) =>
    errorHandler(`There was an error submitting the Claims form`, err);

  const onSubmitSuccess = (values: MalpracticeClaimFormValues) => {
    if (create) {
      addClaim(values);
    } else {
      editClaim(values);
    }
  };

  const claimStatus = useWatch({ control: control, name: 'claimStatus' });
  const isSettled = SETTLED_CLAIM_STATUSES.includes(claimStatus?.value);

  useEffect(() => {
    if (!isSettled) {
      setValue('settlementAmt', null);
      setValue('settlementAmtFormatted', null);
      setValue('settlementDate', '');
    }
  }, [isSettled, setValue]);

  const occurrenceDate = useWatch({ control, name: 'occurrenceDate' });
  const claimDate = useWatch({ control, name: 'claimDate' });
  const settlementDate = useWatch({ control, name: 'settlementDate' });

  useEffect(() => {
    if (formState.isSubmitted) {
      trigger(['occurrenceDate', 'claimDate', 'settlementDate']);
    }
  }, [occurrenceDate, claimDate, settlementDate, formState.isSubmitted, trigger]);

  useServerWarnings(methods, claimForm.warnings, VALID_FIELDS);

  return (
    <FormProvider {...methods}>
      <form
        data-cy={TAGS.form}
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
        id="claims-form"
      >
        <FormLayout className="bg-white text-content-primary">
          <Section componentsPerLine={2}>
            <Row>
              <Select
                name={'providerInvolvement'}
                label={'Provider Involvement'}
                cyTag={TAGS.providerInvolvementSelect}
                options={PROVIDER_INVOLVEMENT_OPTIONS}
                required
              />
            </Row>
            <Row>
              <Select
                name={'claimStatus'}
                label={'Claim Status'}
                cyTag={TAGS.claimStatusSelect}
                options={CLAIM_STATUS_OPTIONS}
                required
              />
            </Row>
            <Row>
              <TextInput
                name={'liabilityCarrier'}
                label={'Liability Carrier'}
                cyTag={TAGS.liabilityCarrierInput}
                required
              />
            </Row>
            <Row>
              <MoneyInput
                name={'settlementAmt'}
                label={'Settlement Amount'}
                cyTag={TAGS.settlementAmtInput}
                disabled={!isSettled}
                required={isSettled}
              />
            </Row>
          </Section>
          <Section componentsPerLine={2}>
            <Row>
              <DatePicker
                label={'Occurrence Date'}
                name={'occurrenceDate'}
                cyTag={TAGS.occurrenceDatePicker}
                minDate={MALPRACTICE_CLAIM_MIN_DATE}
                required
              />
            </Row>
            <Row>
              <DatePicker
                label={'Claim Date'}
                name={'claimDate'}
                cyTag={TAGS.claimDatePicker}
                minDate={MALPRACTICE_CLAIM_MIN_DATE}
                required
              />
            </Row>
            <Row>
              <DatePicker
                label={'Settlement Date'}
                name={'settlementDate'}
                cyTag={TAGS.settlementDatePicker}
                disabled={!isSettled}
                minDate={MALPRACTICE_CLAIM_MIN_DATE}
                required={isSettled}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextArea
                cyTag={TAGS.allegationsTxtInput}
                name={'allegationsTxt'}
                label={'Allegations'}
                description={'Details of the allegations'}
                required
              />
            </Row>
            <Row>
              <TextArea
                cyTag={TAGS.allegedInjuryTxtInput}
                name={'allegedInjuryTxt'}
                label={'Alleged Injury'}
                description={'Details of the alleged injury'}
                required
              />
            </Row>
            <Row>
              <TextArea
                cyTag={TAGS.detailsTxtInput}
                name={'details'}
                label={'Claim Details'}
                description={'Details of the claim'}
              />
            </Row>
          </Section>
          <ButtonFooter
            secondaryButton={{
              label: 'Cancel',
              cyTag: TAGS.cancelBtn,
              onClick: handleClose,
            }}
            primaryButton={{ label: 'Save', cyTag: TAGS.submitBtn, isSubmit: true }}
          />
        </FormLayout>
      </form>
    </FormProvider>
  );
};
